import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
  AtolDeviceInfo,
  AtolDeviceStatus,
  AtolFiscalResult,
  AtolFnStatus,
  AtolRequestAddResult,
  AtolRequestResult,
  AtolRequestResultItem,
  AuthUserModel,
  filterNullish,
  FiscalInfoModel,
  getPaymentValue,
  HxAuthService,
  HxOrderService,
  HxStoreService,
  PaymentType,
  ReceiptModel,
  toFixed,
  uiLabel
} from 'hx-services';
import { BehaviorSubject, firstValueFrom, from, Observable, of, repeat, throwError, zip } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { CashboxService } from '@cashbox-app/service/cashbox.service';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class FiscalizationService {

  private atolPrefix = 'http://127.0.0.1:16732/api/v2';
  private readonly ATOL_BRAND = 'АТОЛ';
  private isLoaded = new BehaviorSubject<FiscalInfoModel | undefined>(undefined);
  private headers = new HttpHeaders({
    'Authorization': 'Basic ' + btoa('admin:Qwerty123456')
  });

  constructor(
    private httpClient: HttpClient,
    private storeService: HxStoreService,
    private orderService: HxOrderService,
    private tr: TranslocoService,
    private auth: HxAuthService,
    private cashboxService: CashboxService,
    private toastr: ToastrService,
  ) {
  }

  getDeviceInfo(): Observable<AtolDeviceInfo> {
    return this.onReady().pipe(mergeMap(fiscalInfo => {
      if (fiscalInfo.deviceBrand === this.ATOL_BRAND) {
        return this.httpClient.post<{ deviceInfo: AtolDeviceInfo }>(`${this.atolPrefix}/operations/queryDeviceInfo`, undefined, { headers: this.headers })
          .pipe(map(data => data.deviceInfo));
      }
      return throwError(() => new Error('unknown device brand'));
    }));
  }

  /**
   * Счётчик ФН: fiscalDocumentNumber растёт, когда документ зарегистрирован в накопителе,
   * то есть ушёл в налоговую — независимо от того, напечатался чек или нет.
   */
  getFnStatus(): Observable<AtolFnStatus> {
    return this.httpClient.post<{ fnStatus: AtolFnStatus }>(`${this.atolPrefix}/operations/queryFnStatus`, undefined, { headers: this.headers })
      .pipe(map(data => data.fnStatus));
  }

  getDeviceStatus(): Observable<AtolDeviceStatus> {
    return this.onReady().pipe(mergeMap(fiscalInfo => {
      if (fiscalInfo.deviceBrand === this.ATOL_BRAND) {
        return this.httpClient.post<{ deviceStatus: AtolDeviceStatus }>(`${this.atolPrefix}/operations/queryDeviceStatus`, undefined, { headers: this.headers })
          .pipe(map(data => data.deviceStatus));
      }
      return throwError(() => new Error('unimplemented'));
    }));
  }

  getFiscalInfoByStoreId(id: number): Observable<FiscalInfoModel> {
    return this.storeService.getFiscalInfoByStoreId(id).pipe(tap(fiscalInfo => {
      this.isLoaded.next(fiscalInfo);
    }));
  }

  closeShift(): Observable<any> {
    const user: AuthUserModel = this.auth.user;
    const req = {
      'uuid': this.uuidv4(),
      'request': [
        {
          'type': 'closeShift',
          'electronically': !CashboxService.isKktPrint(),
          'operator': {
            'name': user.fullname,
            'vatin': user.iin
          }
        }
      ]
    };
    return this.postRequestAndAwaitResult(req);
  }

  openShift(): Observable<any> {
    const user: AuthUserModel = this.auth.user;
    const req = {
      'uuid': this.uuidv4(),
      'request': [
        {
          'type': 'openShift',
          'electronically': !CashboxService.isKktPrint(),
          'operator': {
            'name': user.fullname,
            'vatin': user.iin
          }
        }
      ]
    };
    return this.postRequestAndAwaitResult(req);
  }

  fiscalizeIfOffline(orderId: number): Promise<boolean> {
    return firstValueFrom(this.onReady().pipe(mergeMap(fiscalInfo => {
      if (!fiscalInfo.online && fiscalInfo.enabled) {
        return this.fiscalize(orderId).pipe(map(() => true));
      } else {
        return of(false);
      }
    })));
  }

  saveAtolFiscalizationData(orderId: number, fiscalResult: AtolFiscalResult): Observable<void> {
    return this.getDeviceInfo().pipe(
      map(deviceInfo => {
        fiscalResult.serialNumber = deviceInfo.serial;
        fiscalResult.type = 'atol';
        return fiscalResult;
      }),
      mergeMap(result => this.orderService.saveFiscalizationData(orderId, result))
    );
  }

  /**
   * Задание упало. Документ мог быть уже зарегистрирован в ФН (классика — кончилась
   * бумага при печати): тогда чек в налоговой, и пробивать его второй раз нельзя.
   * Отличаем по счётчику фискальных документов и по результату самого задания.
   */
  /**
   * Заказ числится непробитым. Прежде чем бить заново — спрашиваем у ККТ, нет ли уже
   * выполненного задания с этим uuid: чек мог уйти в налоговую, а печать сорваться.
   * Возвращает true, если фискальные данные нашлись и уже сохранены на сервере.
   */
  claimExistingFiscalization(orderId: number, eventUuid: string): Observable<boolean> {
    return this.rawRequestResult<{ fiscalParams: AtolFiscalResult }>(eventUuid).pipe(
      mergeMap(task => {
        const params = task?.result?.fiscalParams;
        if (!params) {
          return of(false);
        }
        return this.saveAtolFiscalizationData(orderId, params).pipe(map(() => true));
      })
    );
  }

  recoverAfterFailure(uuid: string, fnBefore: AtolFnStatus | undefined, err: any): Observable<AtolFiscalResult> {
    return this.rawRequestResult<{ fiscalParams: AtolFiscalResult }>(uuid).pipe(
      mergeMap(task => {
        const params = task?.result?.fiscalParams;
        if (params) {
          // ККТ вернула реквизиты вместе с ошибкой — документ есть, печать не удалась
          this.toastr.warning(this.tr.translate('fiscalization.registeredNotPrinted'));
          return of(params);
        }
        return this.getFnStatus().pipe(
          mergeMap(fnAfter => {
            const registered = fnBefore !== undefined && fnAfter.fiscalDocumentNumber > fnBefore.fiscalDocumentNumber;
            if (!registered) {
              // документ не регистрировался — ошибка настоящая, чек можно пробивать заново
              return throwError(() => err);
            }
            // реквизитов нет, но счётчик ФН вырос: сохраняем то, что знаем, лишь бы
            // заказ не ушёл на повторное пробитие
            this.toastr.warning(this.tr.translate('fiscalization.registeredNotPrinted'));
            return of({
              type: 'atol',
              fiscalDocumentNumber: fnAfter.fiscalDocumentNumber,
              fiscalReceiptNumber: fnAfter.fiscalReceiptNumber,
            } as AtolFiscalResult);
          }),
          catchError(() => throwError(() => err))
        );
      }),
      catchError(fnErr => fnErr === err ? throwError(() => err) : throwError(() => err))
    );
  }

  /** Результат задания как есть, без интерпретации статуса — нужен при разборе ошибок. */
  rawRequestResult<T>(uuid: string): Observable<AtolRequestResultItem<T> | undefined> {
    return this.httpClient.get<AtolRequestResult<T>>(`${this.atolPrefix}/requests/${uuid}`, { headers: this.headers })
      .pipe(
        map(res => res.results?.[0]),
        catchError(() => of(undefined))
      );
  }

  getRequestResult<T>(uuid: string): Observable<T> {
    return this.httpClient.get<AtolRequestResult<T>>(`${this.atolPrefix}/requests/${uuid}`, { headers: this.headers })
      .pipe(filter(res => {
          if (res.results) {
            const result = res.results[0];
            if (result.error.code === 0 && result.status === 'ready') {
              return true;
            }
            if (result.status === 'error') {
              throw new HttpErrorResponse({error: result.error});
            }
          }
          throw new HttpErrorResponse({error: {code: 1000, description: 'atol.task.notReady'}});
        }),
        map(res => res.results[0].result));
  }

  private isOnline(): Observable<boolean> {
    return this.onReady().pipe(map(info => info.online));
  }

  private fiscalize(orderId: number): Observable<void> {
    return zip(this.onReady(), from(this.cashboxService.getFiscalPaperState())).pipe(
      mergeMap(([fiscalInfo, paperState]) => {
        if (fiscalInfo.deviceBrand !== this.ATOL_BRAND) {
          return throwError(() => new Error('unimplemented'));
        }
        if (!paperState.ready) {
          this.toastr.error(this.tr.translate('fiscalization.noFiscalPaper'));
          return throwError(() => new Error('no fiscal paper'));
        }
        return this.getDeviceStatus().pipe(
          mergeMap(deviceStatus => {
            if (CashboxService.isKktPrint() && !deviceStatus.paperPresent) {
              this.toastr.error(this.tr.translate('fiscalization.noFiscalPaper'));
              this.cashboxService.setFiscalPaperState(false)
                .then(() => this.cashboxService.handleFiscalPaperReadyEvent(false));
              return throwError(() => new Error('no fiscal paper'));
            }
            if (deviceStatus.shift !== 'opened') {
              return this.openShift();
            } else {
              return of(undefined);
            }
          }),
          mergeMap(() => this.orderService.getOrderReceipt({orderId: orderId})),
          map(receipt => this.toAtolFiscalizeRequest(receipt, fiscalInfo)),
          // счётчик ФН до отправки: по нему потом отличим «чек ушёл, но не напечатался»
          // от «чек вообще не регистрировался»
          mergeMap(fiscalizeReq => this.getFnStatus().pipe(
            map(fnBefore => ({fiscalizeReq, fnBefore})),
            catchError(() => of({fiscalizeReq, fnBefore: undefined as AtolFnStatus | undefined}))
          )),
          mergeMap(({fiscalizeReq, fnBefore}) => this.postRequestAndAwaitResult<{ fiscalParams: AtolFiscalResult }>(fiscalizeReq).pipe(
            map(res => res.fiscalParams),
            catchError(err => this.recoverAfterFailure(fiscalizeReq.uuid, fnBefore, err))
          )),
          mergeMap(fiscalResult => this.saveAtolFiscalizationData(orderId, fiscalResult))
        );
      })
    );
  }

  private uuidv4() {
    // @ts-ignore
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      // eslint-disable-next-line no-bitwise
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
  }

  private onReady(): Observable<FiscalInfoModel> {
    return this.isLoaded.pipe(filterNullish(), take(1));
  }

  private postRequestAndAwaitResult<T>(request: any): Observable<T> {
    return this.httpClient.post<AtolRequestAddResult>(`${this.atolPrefix}/requests`, request, { headers: this.headers }).pipe(
      // 409 — задание с таким uuid уже есть. Второй раз тот же чек ККТ не пробьёт,
      // значит забираем результат существующего задания вместо новой отправки.
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          return of({uuid: request.uuid} as AtolRequestAddResult);
        }
        return throwError(() => err);
      }),
      mergeMap(task => this.awaitTaskResult<T>(task.uuid))
    );
  }

  private awaitTaskResult<T>(uuid: string): Observable<T> {
    let callCounter = 0;
    return this.httpClient.get<AtolRequestResult<T>>(`${this.atolPrefix}/requests/${uuid}`, { headers: this.headers })
      .pipe(
        repeat({delay: 300}),
        filter(res => {
          callCounter += 1;
          if (callCounter === 30) {
            throw new Error('call.limit');
          }
          if (res.results) {
            const result = res.results[0];
            if (result.error.code === 0 && result.status === 'ready') {
              return true;
            }
            if (result.status === 'error') {
              //44 = Нет бумаги
              if (result.error.code === 44) {
                this.cashboxService.setFiscalPaperState(false)
                  .then(() => this.cashboxService.handleFiscalPaperReadyEvent(false));
              }
              throw new Error(JSON.stringify(result.error));
            }
            // очередь встала или задание сняли: результат неизвестен, ждать бессмысленно
            if (result.status === 'blocked' || result.status === 'canceled' || result.status === 'interrupted') {
              throw new Error(JSON.stringify({code: result.error?.code ?? 1000, description: `atol.task.${result.status}`}));
            }
          }
          return false;
        }),
        map(res => res.results[0].result),
        take(1)
      );
  }

  private toAtolFiscalizeRequest(receipt: ReceiptModel, fiscalInfo: FiscalInfoModel): any {
    const user = this.auth.user;

    const coinAmount =  getPaymentValue(receipt.payments, PaymentType.COIN);
    const fixedAmount = 10;
    let leftCoinAmount = Math.abs(coinAmount);
    const items = receipt.products.map(product => {
      const productDiscounts = receipt.discounts.filter(dsc => dsc.enabled && !dsc.removed && dsc.productId === product.id);
      const discount = toFixed(productDiscounts.reduce((current, dsc) => current + dsc.value, 0));
      let amount = toFixed(Math.abs(product.value) - discount);
      const quantity = Math.abs(product.amount);

      let finalAmount: number;
      if (leftCoinAmount > 0) {
        if (leftCoinAmount > amount) {
          finalAmount = fixedAmount;
          leftCoinAmount -= amount - finalAmount;
        } else {
          finalAmount = amount - leftCoinAmount;
          leftCoinAmount = 0;
        }
      } else {
        finalAmount = amount;
      }

      let uomUnitId;
      if (product.unitOfMeasureId === 1 || product.unitOfMeasureId === 7 || product.unitOfMeasureId === 8) {
        uomUnitId = 0;
      } else if (product.unitOfMeasureId === 3) {
        uomUnitId = 10;
      } else if (product.unitOfMeasureId === 2) {
        uomUnitId = 11;
      } else if (product.unitOfMeasureId === 5) {
        uomUnitId = 41;
      } else {
        uomUnitId = 255;
      }

      return {
        type: 'position',
        name: uiLabel(this.tr.getActiveLang(), product.title),
        price: toFixed(finalAmount/quantity),
        quantity: quantity,
        amount: finalAmount,
        infoDiscountAmount: discount,
        tax: {type: fiscalInfo.taxType},
        paymentMethod: 'fullPayment',
        paymentObject: 'commodity',
        measurementUnit: uomUnitId,
      };
    });
    /*
    let totalDiscount = Math.abs(receipt.subTotal) - Math.abs(receipt.total);
    const minimumPrice = 20;
    if (totalDiscount > 0) {
      items.forEach(item => {
        let discount;
        let priceValueWithDiscount = item.amount;
        if (priceValueWithDiscount <= totalDiscount) {
          totalDiscount = totalDiscount - priceValueWithDiscount + minimumPrice;
          priceValueWithDiscount = minimumPrice;

          discount = item.amount - minimumPrice;
        } else {
          priceValueWithDiscount -= totalDiscount;

          discount = totalDiscount;
          totalDiscount = 0.0;
        }

        // decimal point ge 3 (0.125 is NOT ok, 0.05 is OK)
        if ((toFixed(priceValueWithDiscount / item.quantity, 5) % 1 + '').length > 4) {

        }

        item.amount = toFixed(priceValueWithDiscount);
        item.infoDiscountAmount = toFixed(discount);
        item.price = toFixed(priceValueWithDiscount / item.quantity, 2);
      });
    }*/
    const cash = Math.abs(getPaymentValue(receipt.payments, PaymentType.CASH));
    const card = Math.abs(receipt.total) - Math.abs(cash) - Math.abs(coinAmount);
    const orderTotal = Math.abs(receipt.total) - Math.abs(coinAmount);
    const payments = [];
    if (cash) {
      payments.push({
        type: 'cash',
        sum: toFixed(cash)
      });
    }
    if (card) {
      payments.push({
        type: 'electronically',
        sum: toFixed(card)
      });
    }
    const type = receipt.isRefund ? 'sellReturn' : 'sell';
    return {
      uuid: receipt.eventUuid,
      request: [
        {
          type: type,
          electronically: !CashboxService.isKktPrint(),
          taxationType: fiscalInfo.taxationType,
          ignoreNonFiscalPrintErrors: false,
          operator: {
            name: user.fullname,
            vatin: user.iin
          },
          items: items,
          payments: payments,
          total: orderTotal,
        }
      ]
    };
  }
}
